import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { Socket, Channel } from 'phoenix';
import getPhoenixSocketConfig from '@root/configs/phoenixSocket';

interface ChannelHandlers {
  onMessage?: (event: string, payload: unknown) => void;
  onJoin?: () => void;
  onLeave?: () => void;
  onError?: (reason: unknown) => void;
}

interface PhoenixSocketContextValue {
  socket: Socket | null;
  connected: boolean;
  joinChannel: (
    topic: string,
    params?: Record<string, unknown>,
    handlers?: ChannelHandlers,
  ) => Channel | null;
  leaveChannel: (channel: Channel) => void;
}

const NOOP = () => undefined;

const PhoenixSocketContext = createContext<PhoenixSocketContextValue>({
  socket: null,
  connected: false,
  joinChannel: () => null,
  leaveChannel: NOOP,
});

interface User {
  id: string;
  first_name?: string;
}

interface RootState {
  auth: {
    user: User | null;
  };
}

interface StateProps {
  user: User | null;
}

function mapStateToProps(state: RootState): StateProps {
  return { user: state.auth.user };
}

interface PhoenixSocketProviderProps extends StateProps {
  children: React.ReactNode;
}

const PhoenixSocketProviderInner: React.FC<PhoenixSocketProviderProps> = ({
  user,
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const channelsRef = useRef<Map<string, Channel>>(new Map());

  // biome-ignore lint/correctness/useExhaustiveDependencies: socket is intentionally excluded to prevent reconnection loops
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const authToken = localStorage.getItem('authToken');
    let newSocket: Socket | null = null;

    if (user && authToken && !socket) {
      const config = getPhoenixSocketConfig();

      // Detect mixed content: HTTPS page trying to connect to ws://
      const isHttpsPage = window.location.protocol === 'https:';
      const isInsecureWebSocket = config.url.startsWith('ws://');

      if (isHttpsPage && isInsecureWebSocket) {
        console.warn(
          'Phoenix Socket: Cannot connect - page loaded over HTTPS but WebSocket URL uses ws://. ' +
            'Set NEXT_PUBLIC_REALTIME_URL to an HTTPS URL to enable real-time features.',
        );
        return;
      }

      try {
        newSocket = new Socket(config.url, {
          params: { token: authToken },
        });

        newSocket.onOpen(() => {
          console.log('Phoenix Socket: Connected');
          setConnected(true);
        });

        newSocket.onClose(() => {
          console.log('Phoenix Socket: Disconnected');
          setConnected(false);
        });

        newSocket.onError((error) => {
          console.warn('Phoenix Socket: Error', error);
        });

        newSocket.connect();
        setSocket(newSocket);
      } catch (error) {
        console.warn(
          'Phoenix Socket: Connection failed - real-time features will be unavailable',
          error,
        );
      }
    }

    // Copy ref to local variable for cleanup
    const channels = channelsRef.current;

    return () => {
      if (newSocket) {
        console.log('Phoenix Socket: Disconnecting');
        // Leave all channels before disconnecting
        channels.forEach((channel) => {
          channel.leave();
        });
        channels.clear();
        newSocket.disconnect();
        setSocket(null);
        setConnected(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  const joinChannel = useCallback(
    (
      topic: string,
      params: Record<string, unknown> = {},
      handlers: ChannelHandlers = {},
    ): Channel | null => {
      if (!socket) return null;

      const key = `${topic}-${JSON.stringify(params)}`;

      // Return existing channel if already joined
      if (channelsRef.current.has(key)) {
        return channelsRef.current.get(key)!;
      }

      try {
        const channel = socket.channel(topic, params);

        // Set up message handler for all events
        if (handlers.onMessage) {
          // Phoenix channels receive messages via onMessage or specific event handlers
          // We'll set up a generic handler that the caller can use
          channel.onMessage = (event, payload) => {
            handlers.onMessage?.(event, payload);
            return payload;
          };
        }

        channel
          .join()
          .receive('ok', () => {
            console.log(`Phoenix Socket: Joined channel ${topic}`);
            handlers.onJoin?.();
          })
          .receive('error', (reason) => {
            console.warn(
              `Phoenix Socket: Failed to join channel ${topic}`,
              reason,
            );
            handlers.onError?.(reason);
          })
          .receive('timeout', () => {
            console.warn(`Phoenix Socket: Timeout joining channel ${topic}`);
            handlers.onError?.('timeout');
          });

        channel.onClose(() => {
          console.log(`Phoenix Socket: Left channel ${topic}`);
          handlers.onLeave?.();
          channelsRef.current.delete(key);
        });

        channel.onError((reason) => {
          console.warn(`Phoenix Socket: Channel error on ${topic}`, reason);
          handlers.onError?.(reason);
        });

        channelsRef.current.set(key, channel);
        return channel;
      } catch (error) {
        console.warn(
          `Phoenix Socket: Failed to join channel ${topic}. ` +
            'This may be due to mixed content (HTTPS page with ws:// WebSocket). ' +
            'Real-time features will be unavailable.',
          error,
        );
        return null;
      }
    },
    [socket],
  );

  const leaveChannel = useCallback((channel: Channel) => {
    if (!channel) return;
    channel.leave();

    // Remove from tracking
    for (const [key, ch] of channelsRef.current.entries()) {
      if (ch === channel) {
        channelsRef.current.delete(key);
        break;
      }
    }
  }, []);

  return (
    <PhoenixSocketContext.Provider
      value={{ socket, connected, joinChannel, leaveChannel }}
    >
      {children}
    </PhoenixSocketContext.Provider>
  );
};

const PhoenixSocketProvider = connect(mapStateToProps)(
  PhoenixSocketProviderInner,
);

export default PhoenixSocketProvider;

export const usePhoenixSocket = () => useContext(PhoenixSocketContext);
